import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, Check, Storefront, Receipt, Wrench, Package, UsersThree, ChartLine, Gear, Coins, Repeat, CaretDown, Sparkle } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { PERMISSIONS, GROUPS, TEMPLATES, STAFF_DEFAULTS, detectTemplate } from "../lib/permissions";

// Group → icon mapping. Keeps the modal sections visually distinct
// without an admin having to read every header. Picked from Phosphor's
// existing duotone set used elsewhere in the app.
const GROUP_ICONS = {
  "Sales": Receipt,
  "Repair tickets": Wrench,
  "Inventory": Package,
  "Customers": UsersThree,
  "Trade-Ins": Repeat,
  "Store": Storefront,
  "Reports": ChartLine,
  "Settings": Gear,
  "Till": Coins,
};

/**
 * Admin-only modal for setting/editing a staff user's permissions.
 *
 * Two-layer UX:
 *   • Top: a "Quick template" picker (Cashier / Senior staff / Manager
 *     / Custom). Selecting one slams the whole toggle grid to that preset
 *     — admin can then tweak individual switches and the picker flips
 *     to "Custom" automatically.
 *   • Below: every permission grouped by area, rendered as a sturdy
 *     toggle row. Defaults to a Cashier baseline for a fresh staff
 *     user.
 *
 * Authorisation: requires the admin's own PIN (entered at save time) to
 * prevent a stolen session from quietly granting itself refund/void.
 */
export default function PermissionsModal({ user, onClose, onSaved }) {
  const [state, setState] = useState({});
  const [tpl, setTpl] = useState("cashier");
  const [tplOpen, setTplOpen] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Seed from the user's current effective permissions (or the cashier
    // baseline for a brand-new staff). The backend returns the fully
    // materialised map on /users so we don't have to compute defaults.
    const seed = user.permissions && Object.keys(user.permissions).length > 0
      ? { ...user.permissions }
      : { ...STAFF_DEFAULTS };
    setState(seed);
    setTpl(user.permission_template || detectTemplate(seed));
    setAdminPin("");
    setTplOpen(false);
  }, [user]);

  // Re-detect template when toggles change — switches the dropdown to
  // "Custom" the moment an admin deviates from a preset. Lets the modal
  // act as a learning tool ("oh, that's basically the Manager preset").
  const effectiveTpl = useMemo(() => detectTemplate(state), [state]);
  useEffect(() => { setTpl(effectiveTpl); }, [effectiveTpl]);

  // Pre-compute grouped permissions outside of conditional rendering so
  // every render calls the same hooks in the same order — must run
  // before the `if (!user) return null` early-out below.
  const grouped = useMemo(() => {
    const out = {};
    for (const g of GROUPS) out[g] = PERMISSIONS.filter((p) => p.group === g);
    return out;
  }, []);

  if (!user) return null;

  const setKey = (key, value) => setState((prev) => ({ ...prev, [key]: !!value }));

  const applyTemplate = (templateKey) => {
    if (templateKey === "custom") { setTplOpen(false); return; }
    const tplDef = TEMPLATES[templateKey];
    if (!tplDef) return;
    setState({ ...tplDef.permissions });
    setTpl(templateKey);
    setTplOpen(false);
    toast.success(`${tplDef.label} preset applied — tweak any toggle and it becomes "Custom".`);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4,8}$/.test(adminPin)) return toast.error("Enter your admin PIN to authorise");
    setSaving(true);
    try {
      const tplToSend = effectiveTpl;
      await api.put(`/users/${user.id}/permissions`, {
        permissions: state,
        permission_template: tplToSend,
      }, { headers: { "X-Admin-Pin": adminPin } });
      toast.success(`Permissions saved for ${user.name}`);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Count enabled permissions for the summary chip.
  const enabledCount = Object.values(state).filter(Boolean).length;
  const totalCount = PERMISSIONS.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="permissions-modal-overlay"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl rounded-[28px] shadow-2xl shadow-slate-900/25 border border-slate-200/60 overflow-hidden my-8"
        data-testid="permissions-modal"
      >
        {/* ---------- Header ---------- */}
        <div className="px-7 pt-7 pb-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] flex items-center justify-center shadow-md shadow-blue-500/25 shrink-0">
              <ShieldCheck size={20} weight="bold" className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">PERMISSIONS</div>
              <h3 className="font-display font-bold text-[22px] text-slate-900 leading-tight tracking-tight truncate">
                {user.name}
              </h3>
              <div className="text-[12px] text-slate-500 mt-1 truncate">
                {user.email} · {enabledCount}/{totalCount} permissions enabled
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full shrink-0" data-testid="permissions-close">
            <X size={18} weight="bold" className="text-slate-500" />
          </button>
        </div>

        {/* ---------- Quick template picker ---------- */}
        <div className="px-7 pt-5 pb-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Quick template</div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setTplOpen((o) => !o)}
              className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 hover:border-slate-300 bg-white flex items-center justify-between gap-3 transition-colors"
              data-testid="template-dropdown"
            >
              <div className="flex items-center gap-3 min-w-0 text-left">
                <Sparkle size={18} weight="fill" className="text-[#1E5FEB] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-slate-900">
                    {tpl === "custom" ? "Custom" : (TEMPLATES[tpl]?.label || tpl)}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {tpl === "custom" ? "Tweak individual toggles below" : (TEMPLATES[tpl]?.description || "")}
                  </div>
                </div>
              </div>
              <CaretDown size={14} weight="bold" className={`text-slate-400 transition-transform ${tplOpen ? "rotate-180" : ""}`} />
            </button>
            {tplOpen && (
              <div className="absolute z-10 left-0 right-0 mt-2 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                {Object.entries(TEMPLATES).map(([key, t]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyTemplate(key)}
                    data-testid={`template-${key}`}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 ${tpl === key ? "bg-blue-50/60" : ""}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${tpl === key ? "bg-[#1E5FEB] text-white" : "bg-slate-100 text-slate-500"}`}>
                      {tpl === key ? <Check size={14} weight="bold" /> : <Sparkle size={14} weight="bold" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-slate-900">{t.label}</div>
                      <div className="text-[12px] text-slate-500 leading-relaxed">{t.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---------- Permission groups ---------- */}
        <div className="px-7 pb-5 max-h-[60vh] overflow-y-auto space-y-5">
          {GROUPS.map((g) => {
            const Icon = GROUP_ICONS[g] || ShieldCheck;
            const items = grouped[g] || [];
            const enabled = items.filter((it) => state[it.key]).length;
            return (
              <section key={g} data-testid={`permission-group-${g.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon size={14} weight="bold" className="text-slate-600" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-700">{g}</div>
                  <div className="text-[10px] font-mono text-slate-400 ml-1">{enabled}/{items.length}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
                  {items.map((p) => (
                    <PermissionToggle
                      key={p.key}
                      perm={p}
                      checked={!!state[p.key]}
                      onChange={(v) => setKey(p.key, v)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* ---------- Footer: admin PIN + actions ---------- */}
        <div className="px-7 pt-4 pb-6 border-t border-slate-100 bg-slate-50/60 space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Your admin PIN (to authorise)</div>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={adminPin}
                onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
                data-testid="permissions-admin-pin"
                className="w-full h-12 px-4 text-center font-mono font-bold text-xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none"
              />
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="h-12 px-5 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
              <button
                type="submit"
                disabled={saving}
                data-testid="permissions-save"
                className="h-12 px-6 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#143ba6] text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-blue-500/25"
              >
                {saving ? "SAVING…" : "SAVE PERMISSIONS"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

// Single permission row with a beefy iOS-style toggle. Whole row is
// clickable, keyboard-accessible, and uses a 32px hit target on mobile.
function PermissionToggle({ perm, checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={`toggle-${perm.key}`}
      className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-50 transition-colors focus:outline-none focus:bg-blue-50/40"
    >
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900">{perm.label}</div>
        <div className="text-[10.5px] font-mono text-slate-400 mt-0.5">{perm.key}</div>
      </div>
      <div className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${checked ? "bg-[#1E5FEB]" : "bg-slate-300"}`}>
        <div className={`absolute top-0.5 ${checked ? "left-5" : "left-0.5"} w-6 h-6 rounded-full bg-white shadow-md transition-all`} />
      </div>
    </button>
  );
}
